import React, { useState, useCallback } from "react";
import { Button, Spinner } from "reactstrap";
import { useForm, Controller } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { CheckCircle, Lock, Eye, EyeOff, AlertCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import BeartrackLogo from "../../assets/beartrack/beartrack_logo.png";
import { confirmRecovery } from "../../api/auth";
import { showToast } from "../../utils/showToast";
import { persistSessionAndRedirect } from "../../utils/sessionRedirect";
import LoginAside from "./LoginAside";

export default function SetNewPassword() {
  const { t } = useTranslation();
  const { control, handleSubmit, formState: { errors } } = useForm();
  const navigate = useNavigate();
  const { token } = useParams();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [linkExpired, setLinkExpired] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const submitValues = useCallback(async ({ password }) => {
    if (password.length < 6) {
      showToast(t("La contraseña debe tener al menos 6 caracteres."), false);
      return;
    }

    // Mirror of the backend DTO's @MaxLength(128). Without this check the server
    // answers a 400 whose message is a raw class-validator string.
    if (password.length > 128) {
      showToast(t("La contraseña puede tener como máximo 128 caracteres."), false);
      return;
    }

    setLoading(true);
    setSuccess(false);
    try {
      const res = await confirmRecovery({ password, token });

      if (!res?.success) {
        // Enlace vencido o ya usado: no se puede reintentar con este mismo enlace, así que
        // en vez de un toast que invita a insistir se cambia la pantalla y se ofrece pedir
        // uno nuevo. Antes el mensaje decía "Intenta nuevamente" y el usuario quedaba
        // atrapado reintentando algo que no podía funcionar.
        if (res?.error?.code === "INVALID_TOKEN") {
          setLinkExpired(true);
          setLoading(false);
          return;
        }

        // El resto ya lo notificó confirmRecovery con el mensaje del backend.
        setLoading(false);
        return;
      }

      const data = res.data || {};

      if (data.accessToken) {
        // Auto-login: la contraseña se cambió y el usuario es elegible para ingresar.
        showToast(t("¡Contraseña actualizada! Ingresando…"), true);
        const result = persistSessionAndRedirect(data);
        if (!result.ok) {
          showToast(t("Tu contraseña se actualizó. Inicia sesión para continuar."), true);
          setSuccess(true);
          setTimeout(() => navigate("/login"), 2000);
        }
        return;
      }

      // Contraseña cambiada pero sin auto-login (p. ej. suspensión por falta de pago).
      if (data.code === "PAYMENT_BLOCKED") {
        showToast(t("Tu contraseña se actualizó, pero tu cuenta está suspendida por falta de pago."), "warning");
      } else {
        showToast(t("¡Contraseña actualizada con éxito!"), true);
      }
      setSuccess(true);
      setTimeout(() => navigate("/login"), 2000);
    } catch (error) {
      showToast(t("Ocurrió un error, intente nuevamente."), false);
    } finally {
      setLoading(false);
    }
  }, [navigate, token]);

  return (
    <div className="login-split">
      <LoginAside />
      <main className="login-main">
        <div className="login-main__inner">
          <div className="login-logo-container login-logo-container--mobile">
            <img src={BeartrackLogo} alt="BearTrack" className="login-logo" />
          </div>

          <div className="login-header">
            <h1 className="login-title">{t("Escoge tu nueva contraseña")}</h1>
            <p className="login-subtitle">{t("Ingresa una contraseña segura de al menos 6 caracteres.")}</p>
          </div>

          {linkExpired ? (
            <div className="login-success">
              <span className="login-success-icon login-success-icon--warning">
                <AlertCircle size={28} />
              </span>
              <p className="login-success-text">{t("Este enlace ya no es válido")}</p>
              <p className="login-subtitle" style={{ margin: 0 }}>
                {t("Los enlaces de recuperación vencen a las 12 horas y solo pueden usarse una vez. Solicita uno nuevo para continuar.")}
              </p>
              <Button
                type="button"
                className="login-button"
                onClick={() => navigate("/login/recovery")}
              >
                {t("Solicitar un enlace nuevo")}
              </Button>
              <a href="/login" className="login-back-link">{t("Volver al inicio de sesión")}</a>
            </div>
          ) : success ? (
            <div className="login-success">
              <span className="login-success-icon"><CheckCircle size={28} /></span>
              <p className="login-success-text">{t("¡Contraseña actualizada con éxito!")}</p>
              <p className="login-subtitle" style={{ margin: 0 }}>{t("Redirigiendo al inicio de sesión…")}</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit(submitValues)} className="login-form" noValidate>
              <div className="login-form-group">
                <div className="login-field">
                  <label htmlFor="new-password" className="login-label">{t("Nueva contraseña")}</label>
                  <div className="login-input-wrap">
                    <span className="login-input-icon"><Lock size={18} strokeWidth={2} /></span>
                    <Controller
                      name="password"
                      control={control}
                      defaultValue=""
                      rules={{ required: t("Ingrese una contraseña válida.") }}
                      render={({ field }) => (
                        <input
                          {...field}
                          id="new-password"
                          type={showPassword ? "text" : "password"}
                          autoComplete="new-password"
                          className="login-input"
                          placeholder="••••••••"
                        />
                      )}
                    />
                    <button
                      type="button"
                      className="login-toggle"
                      onClick={() => setShowPassword((v) => !v)}
                      aria-label={showPassword ? t("Ocultar contraseña") : t("Mostrar contraseña")}
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  {errors.password && (
                    <div className="login-error" role="alert">
                      <AlertCircle size={14} />
                      {errors.password.message}
                    </div>
                  )}
                </div>
              </div>

              <div className="login-actions">
                <Button
                  type="submit"
                  className={`login-button ${loading ? "loading" : ""}`}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      {t("Actualizando…")}
                    </>
                  ) : (
                    t("Confirmar")
                  )}
                </Button>

                <div className="login-forgot">
                  <a href="/login" className="login-forgot-link">{t("Volver al inicio de sesión")}</a>
                </div>
              </div>
            </form>
          )}
        </div>
      </main>
    </div>
  );
}
